import React, { useState, Fragment } from "react";
import { Select as AntSelect, notification } from "antd";
import styled from "styled-components";
import { getErrorMessage } from "src/utils/errors";
import { isDefined, isNullOrUndefined } from "@clipboard-health/util-ts";
import { CbhSpecialistType } from "@src/appV2/ContactCBHInfo/constants";
import { useUpdateBillingSpecialist } from "@src/appV2/FacilityProfile/api/useUpdateBillingSpecialist";
import { useDeleteBillingSpecialist } from "@src/appV2/FacilityProfile/api/useDeleteBillingSpecialist";
import { useUpdateAccountManager } from "@src/appV2/FacilityProfile/api/useUpdateAccountManager";
import { useDeleteAccountManager } from "@src/appV2/FacilityProfile/api/useDeleteAccountManager";

const { Option } = AntSelect;

const Select = styled(AntSelect)`
  width: 300px;
`;

const Label = styled.label`
  margin-right: 10px;
`;

const openNotification = ({ placement, message, description, type }) => {
  notification[type]({
    message,
    description,
    placement,
  });
};

const getDefaultSpecialist = ({ facility, admins, type }) => {
  const { billingSpecialist, accountManagerId } = facility;
  const specialistId =
    type === CbhSpecialistType.ACCOUNT_MANAGER ? accountManagerId : billingSpecialist;
  if (!specialistId || !admins.length) {
    return { _id: null, email: null };
  }
  return admins.find((admin) => admin._id === specialistId);
};

function SelectCBHSpecialist({ facility, admins, type }) {
  const specialistTitle =
    type === CbhSpecialistType.ACCOUNT_MANAGER ? "Account Manager" : "Billing Specialist";
  const [selectedCBHSpecialist, setSelectedCBHSpecialist] = useState(
    getDefaultSpecialist({ facility, admins, type })
  );

  const updateBillingSpecialistMutation = useUpdateBillingSpecialist();
  const deleteBillingSpecialistMutation = useDeleteBillingSpecialist();
  const updateAccountManagerMutation = useUpdateAccountManager();
  const deleteAccountManagerMutation = useDeleteAccountManager();

  const isLoading =
    type === CbhSpecialistType.ACCOUNT_MANAGER
      ? updateAccountManagerMutation.isLoading || deleteAccountManagerMutation.isLoading
      : updateBillingSpecialistMutation.isLoading || deleteBillingSpecialistMutation.isLoading;

  const handleSelect = async (value) => {
    try {
      if (isNullOrUndefined(value)) {
        if (type === CbhSpecialistType.ACCOUNT_MANAGER) {
          await deleteAccountManagerMutation.mutateAsync({
            workplaceId: facility.userId,
          });
        } else {
          await deleteBillingSpecialistMutation.mutateAsync({
            workplaceId: facility._id,
          });
        }
        setSelectedCBHSpecialist({ _id: null, email: null });
        openNotification({
          placement: "topLeft",
          message: specialistTitle,
          description: `${specialistTitle} successfully deleted`,
          type: "success",
        });
      } else {
        setSelectedCBHSpecialist(admins[value]);
        if (type === CbhSpecialistType.ACCOUNT_MANAGER) {
          await updateAccountManagerMutation.mutateAsync({
            workplaceId: facility.userId,
            userId: admins[value]._id,
          });
        } else {
          await updateBillingSpecialistMutation.mutateAsync({
            workplaceId: facility._id,
            userId: admins[value]._id,
          });
        }
        openNotification({
          placement: "topLeft",
          message: specialistTitle,
          description: `${specialistTitle} successfully updated`,
          type: "success",
        });
      }
    } catch (error) {
      openNotification({
        placement: "topLeft",
        message: specialistTitle,
        description: getErrorMessage(error),
        type: "error",
      });
    }
  };

  return (
    <Fragment>
      <Label>{specialistTitle}</Label>
      <Select
        showSearch
        filterOption={(input, option) =>
          isDefined(option) && option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
        }
        optionFilterProp="children"
        value={selectedCBHSpecialist ? selectedCBHSpecialist.email : ""}
        onChange={handleSelect}
        allowClear
        loading={isLoading}
      >
        {admins.map((admin, index) => (
          <Option key={admin._id} value={index}>
            {admin.email}
          </Option>
        ))}
      </Select>
    </Fragment>
  );
}

export default SelectCBHSpecialist;
